import React, { useState } from 'react';
import { Box, Typography, Checkbox, Chip, Grid, IconButton } from '@material-ui/core';
import CustomizeJsonEditor from '../../../../ui/CustomizeJsonEditor';
import { WizardButton } from '../../../../ui/WizardButton';
import ErrorIcon from '@material-ui/icons/Error';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import JsonViewer from '../../../../ui/JsonViwer';
import SectionDivider from '../../../../ui/SectionDivider';

interface Claim {
    key: string;
    value: any;
}

interface Props {
    parsed: any | null;
    claims: Claim[];
    masked: Record<string, boolean>;
    onJsonEntered?: (json: any) => void;
    onToggleClaim: (key: string) => void;
    onCopy?: (text: string) => void;
}

export default function CredentialClaimsPanel({
    parsed,
    claims,
    masked,
    onJsonEntered,
    onToggleClaim,
    onCopy
}: Props) {
    const [draftJson, setDraftJson] = useState<string>('');
    const [isEditing, setIsEditing] = useState(false);

    return (
        <div>
            <Grid container spacing={2} alignItems="stretch">
                {/* LEFT — Panel */}
                <Grid item xs={12} md={7}>
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12
                        }}>
                        <Typography style={{ fontWeight: 600 }}>Signed Credential</Typography>
                        {parsed && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    backgroundColor: '#e8faf7',
                                    color: '#2ec4b6',
                                    border: '1px solid #b2ede8',
                                    borderRadius: 6,
                                    padding: '2px 10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}>
                                + FROM STEP 2
                            </span>
                        )}
                        {parsed && !isEditing && (
                            <Box style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        parsed && onCopy?.(JSON.stringify(parsed, null, 2))
                                    }
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 6,
                                        padding: 6,
                                        backgroundColor: '#fff'
                                    }}>
                                    <FileCopyOutlinedIcon style={{ fontSize: 16, color: '#555' }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    {parsed === null || isEditing ? (
                        <CustomizeJsonEditor
                            value={draftJson}
                            onChange={setDraftJson}
                            onJsonEntered={(json) => {
                                onJsonEntered?.(json);
                                setIsEditing(false);
                            }}
                        />
                    ) : (
                        <Box>
                            <Box>
                                <JsonViewer content={parsed} />
                            </Box>
                            <Box display="flex" justifyContent="flex-end" style={{ marginTop: 10 }}>
                                <WizardButton
                                    variant="outlined"
                                    style={{ fontSize: 12 }}
                                    onClick={() => {
                                        setDraftJson(JSON.stringify(parsed, null, 2));
                                        setIsEditing(true);
                                    }}>
                                    Edit JSON
                                </WizardButton>
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* RIGHT — Panel */}
                <Grid item xs={12} md={5} style={{ overflow: 'hidden' }}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        style={{
                            height: '100%',
                            maxHeight: 450,
                            overflow: 'hidden'
                        }}>
                        <Typography style={{ fontWeight: 600 }}>Claims</Typography>

                        {claims.length > 0 && <SectionDivider />}

                        {claims.length > 0 ? (
                            <Box
                                style={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    paddingRight: 4
                                }}>
                                {claims.map((c) => {
                                    const isMasked = !!masked[c.key];

                                    return (
                                        <Box
                                            key={c.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 6
                                            }}>
                                            <Chip
                                                label={isMasked ? 'Masked' : 'Exposed'}
                                                size="small"
                                                style={{
                                                    backgroundColor: isMasked
                                                        ? '#2ec4b6'
                                                        : 'transparent',
                                                    color: isMasked ? '#fff' : '#2ec4b6',
                                                    border: '1px solid #2ec4b6',
                                                    minWidth: 80,
                                                    justifyContent: 'center'
                                                }}
                                            />

                                            <Checkbox
                                                checked={isMasked}
                                                onChange={() => onToggleClaim(c.key)}
                                                color="primary"
                                                size="small"
                                            />

                                            <Typography
                                                variant="body2"
                                                style={{
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                {c.key}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    border: '1px dotted #1eb89a',
                                    borderRadius: 8,
                                    padding: '20px 24px',
                                    flex: 1
                                }}>
                                <Typography
                                    variant="caption"
                                    style={{
                                        color: '#888',
                                        marginBottom: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4
                                    }}>
                                    <span>
                                        <ErrorIcon
                                            style={{ display: 'flex', alignItems: 'center' }}
                                            fontSize="small"
                                        />
                                    </span>{' '}
                                    Waiting for credential
                                </Typography>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ marginBottom: 12 }}>
                                    <Typography variant="caption" style={{ color: '#888' }}>
                                        Paste JSON
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{ color: '#555', margin: '0 8px' }}>
                                        →
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#888' }}>
                                        Use this
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{ color: '#555', margin: '0 8px' }}>
                                        →
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#888' }}>
                                        Toggle claims
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                    style={{ color: '#888', textAlign: 'center', lineHeight: 1.5 }}>
                                    Each field will appear here as a toggleable claim you can expose
                                    or mask.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
}
